/**
 * Growth Reminders Page
 * Full-page view listing all user's growth reminders in a grid.
 * Route: /my-rituality/reminders
 * Reuses existing QuoteCard, AddReminderModal, EditReminderModal components
 * and useGrowthReminders hook.
 */

import { ChevronRight, Plus } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { useTheme } from "@/context/ThemeContext"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useIsMobile } from "@/hooks/useIsMobile"
import { useTranslation } from "@/language"
import { AddReminderModal } from "../components/AddReminderModal"
import { EditReminderModal } from "../components/EditReminderModal"
import { QuoteCard } from "../components/QuoteCard"
import { useGrowthReminders } from "../hooks/useGrowthReminders"

export function RemindersPage() {
  const { theme, setTheme } = useTheme()
  const previousThemeRef = useRef(theme)
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { t } = useTranslation("myRituality")
  const user = useAuthStore(state => state.user)
  const { reminders, isLoading, deleteReminder } = useGrowthReminders()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingReminder, setEditingReminder] = useState<{ id: string; text: string } | null>(null)

  const userReminders = useMemo(() => reminders.filter(r => !r.isSystemDefault), [reminders])

  const handleEdit = (id: string, text: string) => {
    setEditingReminder({ id, text })
  }

  const handleDelete = (id: string) => {
    deleteReminder(id, {
      onSuccess: () => {
        toast.success("Reminder deleted")
      },
      onError: () => {
        toast.error("Failed to delete reminder")
      },
    })
  }

  // Force dark theme on mount, restore on unmount
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only effect
  useEffect(() => {
    previousThemeRef.current = theme
    if (theme !== "dark") {
      setTheme("dark")
    }
    return () => {
      if (previousThemeRef.current !== "dark") {
        setTheme(previousThemeRef.current)
      }
    }
  }, [])

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-32 bg-white/5 rounded animate-pulse" />
      </div>
    )
  }

  return (
    <div className="min-h-screen relative">
      <div className="md:pt-8 pb-15">
        {/* Breadcrumb Navigation */}
        <div className="max-w-360 mx-auto px-5 md:px-17.5 pt-4 md:pt-0">
          <nav className="text-[13px] text-text-primary font-montserrat hidden md:flex items-center gap-[5px]">
            <Link to="/" className="opacity-90 hover:opacity-80 transition-opacity">
              {t("remindersPage.breadcrumb.home")}
            </Link>
            <ChevronRight size={15} className="opacity-50" />
            <Link to="/my-rituality" className="opacity-90 hover:opacity-80 transition-opacity">
              {t("remindersPage.breadcrumb.myRituality")}
            </Link>
            <ChevronRight size={15} className="opacity-50" />
            <span className="opacity-40">{t("remindersPage.breadcrumb.myReminders")}</span>
          </nav>
        </div>

        {/* Mobile Header */}
        {isMobile && (
          <div className="px-5 pt-3 pb-2">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => navigate("/my-rituality")}
                className="text-sm text-primary flex items-center gap-1 opacity-90 hover:opacity-80 transition-opacity cursor-pointer"
              >
                <ChevronRight size={16} className="rotate-180" />
                <span>{t("remindersPage.breadcrumb.myRituality")}</span>
              </button>
            </div>
          </div>
        )}

        {/* Title Row */}
        <div className="max-w-360 mx-auto px-5 md:px-17.5 mt-5 md:mt-9">
          <div className="flex items-center justify-between">
            <h1 className="text-xl md:text-[30px] font-semibold uppercase opacity-90 text-primary leading-[37px]">
              {t("remindersPage.title")}
            </h1>
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="hidden md:flex items-center gap-[5px] text-sm font-semibold text-primary hover:opacity-80 transition-opacity cursor-pointer"
            >
              <span className="p-1 bg-white rounded-full">
                <Plus className="w-4 h-4 text-[#000000]" />
              </span>
              <span>{t("reminders.addReminder")}</span>
            </button>
          </div>
        </div>

        {/* Reminders Grid */}
        <div className="max-w-360 mx-auto px-5 md:px-17.5 mt-8 md:mt-8">
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="w-full aspect-square rounded-tl-[40px] rounded-br-[40px] bg-white/5 animate-pulse"
                />
              ))}
            </div>
          ) : userReminders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <p className="text-lg font-semibold text-primary opacity-90 mb-2">
                {t("reminders.empty")}
              </p>
              <p className="text-sm text-primary opacity-60 mb-8">
                {t("reminders.emptyDescription")}
              </p>
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="flex items-center gap-1.5 px-6 py-3 bg-white text-black rounded-full text-sm font-semibold uppercase hover:opacity-90 transition-opacity cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>{t("reminders.addReminder")}</span>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {userReminders.map(reminder => (
                <QuoteCard
                  key={reminder.id}
                  text={reminder.text}
                  backgroundImageKey={reminder.backgroundImageKey ?? "abstract-1"}
                  showMenu={true}
                  onEdit={() => handleEdit(reminder.id, reminder.text)}
                  onDelete={() => handleDelete(reminder.id)}
                  className="w-full h-auto aspect-square"
                />
              ))}
            </div>
          )}
        </div>

        {/* Mobile Add Reminder FAB */}
        {isMobile && !isLoading && userReminders.length > 0 && (
          <div className="fixed bottom-6 right-6 z-40">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="flex items-center justify-center w-14 h-14 bg-white rounded-full shadow-lg hover:opacity-90 transition-opacity cursor-pointer"
            >
              <Plus className="w-6 h-6 text-black" />
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddReminderModal open={isModalOpen} onOpenChange={setIsModalOpen} />
      <EditReminderModal
        open={!!editingReminder}
        onOpenChange={open => !open && setEditingReminder(null)}
        reminderId={editingReminder?.id}
        defaultText={editingReminder?.text}
      />
    </div>
  )
}

export default RemindersPage
